/** Hostnames that should use generic portal login (no tenant lookup). */
const DEFAULT_GENERIC_PORTAL_HOSTS = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '[::1]',
  'portal.valt.africa',
]

function parseGenericHostsFromEnv(): string[] {
  const raw = import.meta.env.VITE_PORTAL_GENERIC_HOSTS
  if (!raw || typeof raw !== 'string') return []
  return raw
    .split(',')
    .map((h) => stripHost(h))
    .filter(Boolean)
}

function getGenericPortalHosts(): Set<string> {
  return new Set([...DEFAULT_GENERIC_PORTAL_HOSTS, ...parseGenericHostsFromEnv()])
}

function stripHost(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .split(':')[0]
}

function isIpAddressHost(hostname: string): boolean {
  const host = stripHost(hostname)
  if (!host) return false
  if (host.startsWith('[') && host.endsWith(']')) return true
  if (host.includes(':')) return true
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
}

/** True when the browser host should use generic login (any client may sign in). */
export function isGenericPortalHost(hostname: string): boolean {
  const host = stripHost(hostname)
  if (!host) return true
  if (isIpAddressHost(host)) return true
  return getGenericPortalHosts().has(host)
}

/** True when portal-config should be fetched for the current browser host. */
export function shouldFetchPortalConfig(hostname?: string): boolean {
  if (import.meta.env.VITE_PORTAL_FORCE_GENERIC_LOGIN === 'true') return false

  const host = stripHost(hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''))
  if (!host || isGenericPortalHost(host)) return false

  return resolvePortalConfigHost(hostname) !== null
}

/**
 * Hostname sent to GET /client/portal-config and login `portal_host`.
 * Returns null for generic hosts. Set VITE_PORTAL_DEV_HOST to test a tenant locally.
 */
export function resolvePortalConfigHost(hostname?: string): string | null {
  const devOverride = import.meta.env.VITE_PORTAL_DEV_HOST?.trim()
  if (devOverride) {
    const override = stripHost(devOverride)
    if (override && !isGenericPortalHost(override)) return override
  }

  const raw = stripHost(hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''))
  if (!raw || isGenericPortalHost(raw)) return null
  return raw
}

/** portal_host for login — omitted only on generic hosts. Always sent on custom domains. */
export function resolvePortalLoginHost(): string | undefined {
  return resolvePortalConfigHost() ?? undefined
}
